import { PASSENGER_KEYS, ROOM_KEYS } from "../Components/PassengersData/passengerKeys";
import { getFinalIlsFromOurTotalFareForDisplay } from "./packages/computeRoomDisplayPrice";
import {
  isVacationPackagesFlightsHotelAgentDeal,
  vacationPackageGlobalAgentMarkupIls,
} from "./vacationPackageRoomAgentMarkup";
import { readVpRoomCardRawPwccIls } from "./resolveVpRoomCardDisplayPrice";
import { resolveManualRoomDisplayPriceIls } from "./vpManualRoomMarkup";
import {
  findExtendedDealRowForAddRoomOccupancy,
  buildVacationPackageRoomPricingSnapshot,
  occupancySignature,
  pickDealSeq,
  readEngineBasePriceWithCommissionIls,
  readRoomPricingSnapshot,
  resolveVpAddRoomOccupancyPrice,
} from "./vacationPackageRoomPricingSSOT";

export const VP_MANUAL_ADD_ROOM_PRICE_SOURCE = "manual_add_room";

function _num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function _round2(n) {
  return Math.round(Number(n) * 100) / 100;
}

/**
 * Persistable provenance for a manual-add room — no functions or nested fare blobs.
 */
export function compactVpManualAddRoomPricingSnapshot(snap, extras = {}) {
  if (!snap || typeof snap !== "object") {
    if (extras.basePriceWithCommissionIls == null && extras.finalPriceIls == null) {
      return null;
    }
  }
  const src = snap && typeof snap === "object" ? snap : {};
  const base = _num(src.basePriceWithCommissionIls ?? extras.basePriceWithCommissionIls);
  const finalPriceIls = _num(src.finalPriceIls ?? extras.finalPriceIls);
  if ((base == null || base <= 0) && (finalPriceIls == null || finalPriceIls <= 0)) {
    return null;
  }
  return {
    status: src.status === "ready" || finalPriceIls > 0 || base > 0 ? "ready" : src.status ?? "ready",
    productType: src.productType ?? "vacation_package_room",
    roomType: src.roomType ?? extras.roomType ?? null,
    dealSeq: src.dealSeq != null ? String(src.dealSeq) : extras.dealSeq != null ? String(extras.dealSeq) : null,
    occupancySignature: src.occupancySignature ?? extras.occupancySignature ?? null,
    supplierGross: _num(src.supplierGross ?? extras.supplierGross),
    basePriceWithCommissionIls: base,
    finalPriceIls,
    currency: src.currency ?? extras.currency ?? "ILS",
    fxRate: _num(src.fxRate ?? extras.fxRate),
    via: src.via ?? extras.via ?? null,
    roomSet: src.roomSet ?? extras.roomSet ?? null,
    roomId: src.roomId ?? extras.dealSeq ?? src.dealSeq ?? null,
    originalCurrency: src.originalCurrency ?? extras.originalCurrency ?? null,
    ilsDisplayRounded:
      src.ilsDisplayRounded ??
      (finalPriceIls != null ? Math.round(finalPriceIls) : null),
    usdDisplayIntegerStr: src.usdDisplayIntegerStr ?? null,
    usdRaw: src.usdRaw ?? null,
    source: src.source ?? "agent_room_ourFareDetails_ssot",
  };
}

export function buildVpManualAddRoomFreezeFields({
  occupancy = null,
  roomSuggestion = null,
  ourFareDetails = null,
  pricingSnapshot = null,
} = {}) {
  const dealSeq = pickDealSeq(roomSuggestion) ?? pickDealSeq(occupancy);
  const roomType =
    roomSuggestion?.roomType ??
    roomSuggestion?.RoomType ??
    occupancy?.roomType ??
    pricingSnapshot?.roomType ??
    null;
  const baseFromFare = readEngineBasePriceWithCommissionIls(ourFareDetails);
  const compact = compactVpManualAddRoomPricingSnapshot(pricingSnapshot, {
    roomType,
    dealSeq,
    occupancySignature:
      pricingSnapshot?.occupancySignature ?? occupancySignature(occupancy),
    supplierGross:
      _num(roomSuggestion?.totalFare) ??
      _num(ourFareDetails?.ourTotalFare?.netPrice),
    basePriceWithCommissionIls:
      _num(pricingSnapshot?.basePriceWithCommissionIls) ?? baseFromFare,
    finalPriceIls: _num(pricingSnapshot?.finalPriceIls),
    fxRate: _num(ourFareDetails?.ourTotalFare?.fx_rate),
    via: pricingSnapshot?.via ?? "manual_add_room_freeze",
    roomSet: occupancy?.roomSet ?? roomSuggestion?.roomSet ?? pricingSnapshot?.roomSet,
    currency: "ILS",
    originalCurrency: ourFareDetails?.ourTotalFare?.originalCurrency ?? null,
  });
  const selectedAddRoomBasePriceIls =
    compact?.basePriceWithCommissionIls ?? baseFromFare;
  const selectedAddRoomPriceIls =
    compact?.finalPriceIls ??
    _num(ourFareDetails?.ourTotalFare?.priceWithCommissionAndCreditCardFee) ??
    selectedAddRoomBasePriceIls;
  return {
    selectedAddRoomBasePriceIls:
      selectedAddRoomBasePriceIls != null ? _round2(selectedAddRoomBasePriceIls) : null,
    selectedAddRoomPriceIls:
      selectedAddRoomPriceIls != null ? _round2(selectedAddRoomPriceIls) : null,
    selectedAddRoomDealSeq: compact?.dealSeq ?? dealSeq ?? null,
    selectedAddRoomRoomType: compact?.roomType ?? roomType ?? null,
    selectedAddRoomOccupancySignature:
      compact?.occupancySignature ?? occupancySignature(occupancy),
    pricingSnapshot: compact,
  };
}

export function applyVpManualAddRoomFreezeToRoomAndSlot(slot, freeze) {
  if (!slot || !freeze) return slot;
  if (!slot.room) slot.room = {};
  const room = slot.room;
  if (freeze.selectedAddRoomBasePriceIls != null) {
    room.selectedAddRoomBasePriceIls = freeze.selectedAddRoomBasePriceIls;
    slot.selectedAddRoomBasePriceIls = freeze.selectedAddRoomBasePriceIls;
  }
  if (freeze.selectedAddRoomPriceIls != null) {
    room.selectedAddRoomPriceIls = freeze.selectedAddRoomPriceIls;
    slot.selectedAddRoomPriceIls = freeze.selectedAddRoomPriceIls;
  }
  if (freeze.selectedAddRoomDealSeq != null) {
    room.selectedAddRoomDealSeq = freeze.selectedAddRoomDealSeq;
    slot.selectedAddRoomDealSeq = freeze.selectedAddRoomDealSeq;
  }
  if (freeze.selectedAddRoomRoomType != null) {
    room.selectedAddRoomRoomType = freeze.selectedAddRoomRoomType;
    slot.selectedAddRoomRoomType = freeze.selectedAddRoomRoomType;
  }
  if (freeze.selectedAddRoomOccupancySignature != null) {
    room.selectedAddRoomOccupancySignature = freeze.selectedAddRoomOccupancySignature;
    slot.selectedAddRoomOccupancySignature = freeze.selectedAddRoomOccupancySignature;
  }
  if (freeze.pricingSnapshot) {
    room.pricingSnapshot = freeze.pricingSnapshot;
    slot.pricingSnapshot = freeze.pricingSnapshot;
  }
  return slot;
}

function getFirstFare(adultFare, childFare, numberOfKids, kidsAges) {
  let childFareFR = 0;
  if (Array.isArray(childFare)) {
    for (let i = 0; i < childFare.length; i++) {
      let j = 0;
      for (j = 0; j < kidsAges.length; j++) {
        const age = kidsAges[j];
        if (age >= childFare[i].ageFrom && age <= childFare[i].ageTo) {
          childFareFR += Number(childFare[i].fare);
          break;
        }
      }
      if (j === kidsAges.length) childFareFR += Number(adultFare);
    }
  } else if (!childFare || !childFare.fare) {
    childFareFR = numberOfKids * adultFare;
  } else {
    for (let j = 0; j < kidsAges.length; j++) {
      const age = kidsAges[j];
      if (age >= childFare.ageFrom && age <= childFare.ageTo) {
        childFareFR += Number(childFare.fare);
      } else childFareFR += Number(adultFare);
    }
  }
  return childFareFR;
}

/**
 * Same extended-deal row pick as PackagePriceSelector — anchored to first room type when set.
 * Prefer resolveVpAddRoomOccupancyPrice for full SSOT (public + agent).
 */
export function findBestExtendedDealRoomForOccupancy({
  occupancy,
  extendedDealApiData,
  selectedResult,
  calculateLDSDealFare,
  handleFeeData,
  getCreditCardFeePercent,
  numberOfRooms = 1,
  roomsAndPaxInfo = null,
  publicSitePricingSettings = null,
  agentHotelPricing = null,
  ilsToUsd = null,
}) {
  if (publicSitePricingSettings != null || roomsAndPaxInfo?.length) {
    const resolved = resolveVpAddRoomOccupancyPrice({
      occupancy,
      extendedDealApiData,
      roomsAndPaxInfo,
      selectedResult,
      calculateLDSDealFare,
      handleFeeData,
      getCreditCardFeePercent,
      numberOfRooms,
      publicSitePricingSettings,
      agentHotelPricing,
      ilsToUsd,
    });
    if (resolved?.roomSuggestion && resolved?.ourFareDetails) {
      return {
        roomSuggestion: resolved.roomSuggestion,
        ourFareDetails: resolved.ourFareDetails,
        displayIls: resolved.price,
        pricingSnapshot: resolved.pricingSnapshot,
      };
    }
    if (resolved?.pricingSnapshot && Number(resolved.price) > 0) {
      return {
        roomSuggestion: findExtendedDealRowForAddRoomOccupancy({
          occupancy,
          extendedDealApiData,
        }),
        ourFareDetails: resolved.ourFareDetails ?? roomsAndPaxInfo?.[0]?.room?.ourFareDetails ?? null,
        displayIls: resolved.price,
        pricingSnapshot: resolved.pricingSnapshot,
      };
    }
  }

  if (!occupancy || !Array.isArray(extendedDealApiData) || !selectedResult) {
    return null;
  }

  const { fareDetails, dealType, hotel, tour, supplierDetails } = selectedResult;
  const vpAgentMarkupCards = isVacationPackagesFlightsHotelAgentDeal(selectedResult);
  const globalMarkupIlsPreview =
    vpAgentMarkupCards && handleFeeData?.type === "+"
      ? vacationPackageGlobalAgentMarkupIls(handleFeeData)
      : null;

  const roomSuggestion = findExtendedDealRowForAddRoomOccupancy({
    occupancy,
    extendedDealApiData,
  });
  if (!roomSuggestion) return null;

  const {
    childFare = { fare: 0 },
    adultFare,
    infantFare = 0,
    totalFare = 0,
  } = roomSuggestion;

  let calculatedTotalFare = totalFare;
  if (!calculatedTotalFare) {
    const kidsAges = Array(occupancy.kids).fill(10);
    const rawSum =
      Number(adultFare) * occupancy.adults +
      Number(getFirstFare(adultFare, childFare, occupancy.kids, kidsAges)) +
      Number(infantFare) * (occupancy.infants || 0);
    calculatedTotalFare = Math.round(rawSum * 100) / 100;
  } else if (occupancy.infants > 0 && infantFare) {
    calculatedTotalFare =
      Number(calculatedTotalFare) + Number(infantFare) * occupancy.infants;
  }

  const ourFareDetails = calculateLDSDealFare({
    dealFare: {
      adultFare,
      totalFare: calculatedTotalFare,
      childFareExt: childFare,
      infantFare,
    },
    fareDetails,
    dealType,
    hotel,
    tour,
    supplierDetails,
  });

  const tf = ourFareDetails?.ourTotalFare;
  const feeCtx = {
    handleFeeData,
    numberOfRooms,
    getCreditCardFeePercent,
    vacationPackagesPerRoomFixedMarkup:
      vpAgentMarkupCards && handleFeeData?.type === "+",
    perRoomFixedMarkupIls:
      vpAgentMarkupCards && handleFeeData?.type === "+"
        ? globalMarkupIlsPreview
        : null,
  };
  const displayIls = getFinalIlsFromOurTotalFareForDisplay(tf, feeCtx);

  if (displayIls == null || displayIls <= 0) return null;

  return {
    roomSuggestion,
    ourFareDetails,
    displayIls,
    pricingSnapshot: buildVacationPackageRoomPricingSnapshot({
      roomSuggestion,
      ourFareDetails,
      occupancy,
      selectedResult,
      handleFeeData,
      getCreditCardFeePercent,
      numberOfRooms,
      ilsToUsd,
    }),
  };
}

/** @param {object|null|undefined} room @param {object|null|undefined} slot */
export function isVpManualAddedRoom(room, slot = null) {
  return (
    room?.addedFromRoomOptions === true ||
    room?.priceSource === VP_MANUAL_ADD_ROOM_PRICE_SOURCE ||
    slot?.addedFromRoomOptions === true
  );
}

/** @param {object|null|undefined} room @param {object|null|undefined} slot */
export function readVpSelectedAddRoomPriceIls(room, slot = null) {
  const snap = readRoomPricingSnapshot(slot ?? { room, pricingSnapshot: room?.pricingSnapshot });
  if (snap?.finalPriceIls != null) {
    return Math.round(Number(snap.finalPriceIls) * 100) / 100;
  }
  const n = Number(room?.selectedAddRoomPriceIls ?? slot?.selectedAddRoomPriceIls);
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) / 100 : null;
}

/**
 * @returns {{ valid: boolean, priceIls: number|null, via: string|null }}
 */
export function resolveVpRoomLevelDisplayPrice(room, slot = null, handleFeeData = null) {
  const snap = readRoomPricingSnapshot(slot ?? { room, pricingSnapshot: room?.pricingSnapshot });
  if (snap?.finalPriceIls != null && Number(snap.finalPriceIls) > 0) {
    return { valid: true, priceIls: Number(snap.finalPriceIls), via: "pricingSnapshot.finalPriceIls" };
  }
  if (isVpManualAddedRoom(room, slot)) {
    return resolveManualRoomDisplayPriceIls(room, slot, handleFeeData);
  }
  const pwcc = readVpRoomCardRawPwccIls(room);
  if (pwcc != null && pwcc > 0) {
    return { valid: true, priceIls: pwcc, via: "ourFareDetails_pwcc" };
  }
  const addPrice = readVpSelectedAddRoomPriceIls(room, slot);
  if (addPrice != null) {
    return { valid: true, priceIls: addPrice, via: "selectedAddRoomPriceIls" };
  }
  return { valid: false, priceIls: null, via: null };
}

/**
 * Same roomsAndPaxInfo slot PackagePriceSelector.append writes. Pricing comes
 * from findBestExtendedDealRoomForOccupancy — no frontend sum.
 */
export function buildVpManualAddRoomSlot({
  occupancy,
  roomsAndPaxInfo = [],
  slotIndex = null,
  selectedResult = null,
  calculateLDSDealFare = null,
  handleFeeData = null,
  getCreditCardFeePercent = null,
  publicSitePricingSettings = null,
  agentHotelPricing = null,
  ilsToUsd = null,
  extendedDealApiData = null,
} = {}) {
  const index = slotIndex != null ? slotIndex : roomsAndPaxInfo.length;
  const bestMatch = findBestExtendedDealRoomForOccupancy({
    occupancy,
    extendedDealApiData,
    selectedResult,
    calculateLDSDealFare,
    handleFeeData,
    getCreditCardFeePercent,
    numberOfRooms: index + 1,
    roomsAndPaxInfo,
    publicSitePricingSettings,
    agentHotelPricing,
    ilsToUsd,
  });
  const rs = bestMatch?.roomSuggestion;
  if (!rs && !bestMatch?.pricingSnapshot) return null;
  const ourFareDetails = bestMatch?.ourFareDetails ?? null;
  const pricingSnapshot = occupancy?.pricingSnapshot ?? bestMatch?.pricingSnapshot ?? null;
  const freeze = buildVpManualAddRoomFreezeFields({
    occupancy,
    roomSuggestion: rs,
    ourFareDetails,
    pricingSnapshot,
  });
  const selectedAddRoomPriceIls = freeze.selectedAddRoomPriceIls;
  if (!(Number(selectedAddRoomPriceIls) > 0) && !(Number(bestMatch?.displayIls) > 0)) {
    return null;
  }
  const selectedAddRoomBasePriceIls = freeze.selectedAddRoomBasePriceIls;
  const compactSnapshot = freeze.pricingSnapshot;
  const displayedPriceWithMarkupIls =
    Number(ourFareDetails?.ourTotalFare?.priceWithCommissionAndCreditCardFee) > 0
      ? Number(ourFareDetails.ourTotalFare.priceWithCommissionAndCreditCardFee)
      : selectedAddRoomPriceIls;

  return {
    adults: Array.from({ length: occupancy.adults }, (_, i) => ({
      "@seqNum": index * 10 + i + 1,
      [PASSENGER_KEYS.TITLE]: "Mr",
      [PASSENGER_KEYS.FIRSTNAME]: "",
      [PASSENGER_KEYS.LASTNAME]: "",
      [PASSENGER_KEYS.CELL]: "",
      [PASSENGER_KEYS.EMAIL]: "",
      [PASSENGER_KEYS.AGE]: "01/01/1990",
    })),
    kids: Array.from({ length: occupancy.kids }, (_, i) => ({
      "@seqNum": index * 10 + occupancy.adults + i + 1,
      [PASSENGER_KEYS.TITLE]: "CHD",
      [PASSENGER_KEYS.FIRSTNAME]: "",
      [PASSENGER_KEYS.LASTNAME]: "",
      [PASSENGER_KEYS.AGE]: "01/01/2020",
      [PASSENGER_KEYS.KIS_SEARCH_AGE]: 10,
    })),
    infants: Array.from({ length: occupancy.infants || 0 }, (_, i) => ({
      "@seqNum": index * 10 + occupancy.adults + occupancy.kids + i + 1,
      [PASSENGER_KEYS.TITLE]: "INFANT",
      [PASSENGER_KEYS.FIRSTNAME]: "",
      [PASSENGER_KEYS.LASTNAME]: "",
      [PASSENGER_KEYS.AGE]: "01/01/2024",
    })),
    room: {
      [ROOM_KEYS.ROOM_SET]: occupancy.roomSet ?? rs?.roomSet ?? "",
      [ROOM_KEYS.ROOM_TYPE]: occupancy.roomType ?? rs?.roomType ?? rs?.RoomType ?? "",
      [ROOM_KEYS.SEQ_NUM]: rs?.["@seqNum"] ?? rs?.roomId ?? occupancy.dealSeq ?? "",
      ourFareDetails,
      addedFromRoomOptions: true,
      priceSource: VP_MANUAL_ADD_ROOM_PRICE_SOURCE,
      selectedAddRoomPriceIls,
      selectedAddRoomBasePriceIls,
      displayedPriceWithMarkupIls,
      ldsDealId: rs?.ldsDealId ?? null,
      pricingSnapshot: compactSnapshot,
      selectedAddRoomDealSeq: freeze.selectedAddRoomDealSeq,
      selectedAddRoomRoomType: freeze.selectedAddRoomRoomType,
      selectedAddRoomOccupancySignature: freeze.selectedAddRoomOccupancySignature,
    },
    "@seqNum": index + 1,
    agentMarkupManuallyEdited: false,
    addedFromRoomOptions: true,
    pricingSnapshot: compactSnapshot,
    selectedAddRoomPriceIls,
    selectedAddRoomBasePriceIls,
    selectedAddRoomDealSeq: freeze.selectedAddRoomDealSeq,
    selectedAddRoomRoomType: freeze.selectedAddRoomRoomType,
    selectedAddRoomOccupancySignature: freeze.selectedAddRoomOccupancySignature,
    displayedPriceWithMarkupIls,
    selectedPackageId:
      selectedResult?.package?.packageId ?? selectedResult?.packageId ?? null,
  };
}
